import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import type { DrawerImage } from "./types";

export type { DrawerImage };

export function ProductImageGallery({ images }: { images: DrawerImage[] }) {
  const [index, setIndex] = useState(0);
  const thumbItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const count = images.length;

  useEffect(() => {
    setIndex(0);
  }, [images]);

  useEffect(() => {
    thumbItemRefs.current[index]?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
    });
  }, [index]);

  const active = images[index];

  return (
    <Box sx={{ display: "flex", gap: 1.5, p: 2.5 }}>
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box
          sx={{
            width: "100%",
            height: 248,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {active ? (
            <Box
              component="img"
              src={active.url}
              alt={active.alt}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                p: 1,
              }}
            />
          ) : (
            <ImageOutlinedIcon
              sx={{ fontSize: 56, color: "action.disabled", opacity: 0.5 }}
            />
          )}
        </Box>
        {count > 1 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 6,
              right: 6,
              display: "flex",
              alignItems: "center",
              gap: 0.25,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: 13,
                color: "text.secondary",
                bgcolor: "background.paper",
                borderRadius: 0.5,
                px: 0.5,
              }}
            >
              {index + 1}/{count}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIndex((i) => (i - 1 + count) % count)}
              aria-label="Previous image"
              sx={{
                width: 22,
                height: 22,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setIndex((i) => (i + 1) % count)}
              aria-label="Next image"
              sx={{
                width: 22,
                height: 22,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {count > 1 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexShrink: 0,
            height: 248,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "divider",
              borderRadius: 2,
            },
          }}
        >
          {images.map((image, i) => (
            <Box
              key={`${image.url}-${i}`}
              ref={(el: HTMLDivElement | null) => {
                thumbItemRefs.current[i] = el;
              }}
              role="button"
              tabIndex={0}
              aria-label={`View image ${i + 1}`}
              aria-current={index === i}
              onClick={() => setIndex(i)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIndex(i);
                }
              }}
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                bgcolor: "background.paper",
                outline: index === i ? "2px solid" : "none",
                outlineColor: "primary.main",
                "&:focus-visible": {
                  outline: "3px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
                borderRadius: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={image.url}
                alt={image.alt}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  p: 0.5,
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
